/**
 * Перенос `lib/src/utils/exceptions/app_exception.dart`.
 *
 * Dio из порта убран, поэтому `DioException` заменён на [HttpError] —
 * форму ошибки, которую бросает самописный HTTP-слой поверх `fetch`.
 * Набор причин тот же, что у `DioExceptionType`, и отображение
 * «причина транспорта -> [AppExceptionType]» повторяет `fromDioType`
 * один в один.
 *
 * `StackTrace` в Dart — отдельный объект из `catch (e, stk)`. В JS стек
 * живёт на самой ошибке (`Error.stack`), поэтому он переносится строкой,
 * а `StackTrace.current` — функцией [currentStackTrace].
 */

import { ZodError } from 'zod';

import {
  apiResponseModelTryFromResponse,
  type ApiResponseItemParser,
  type ApiResponseModel,
} from '@/domain/models/apiResponseModel';
import type { L10nKey } from '@/l10n/types';
import { getL10n } from '@/l10n/useL10n';

/** Enum representing different types of errors. */
export const AppExceptionType = {
  connectionTimeout: 'connectionTimeout',
  sendTimeout: 'sendTimeout',
  receiveTimeout: 'receiveTimeout',
  badCertificate: 'badCertificate',
  badResponse: 'badResponse',
  cancel: 'cancel',
  connectionError: 'connectionError',
  unknown: 'unknown',
  typeError: 'typeError',
  // `StateError` и `NoSuchMethodError` — чисто дартовые ошибки, в JS их нет
  // и через [AppException.handle] они не появляются. Участники оставлены:
  // на них ссылается разбор причин в `appFailure.ts` и экран диагностики.
  stateError: 'stateError',
  rangeError: 'rangeError',
  formatException: 'formatException',
  noSuchMethodError: 'noSuchMethodError',
} as const;

export type AppExceptionType = (typeof AppExceptionType)[keyof typeof AppExceptionType];

/**
 * `formattedName` — ключ перевода на каждую причину. В Dart это был
 * `switch` по `this`, возвращавший геттер `AppLocalizations`; здесь та же
 * таблица, а полнота её проверяется типом `Record`.
 */
const NAME_KEY_BY_TYPE: Record<AppExceptionType, L10nKey> = {
  connectionTimeout: 'error_connection_timeout',
  badCertificate: 'error_bad_certificate',
  badResponse: 'error_bad_response',
  cancel: 'error_cancel',
  connectionError: 'error_connection_error',
  formatException: 'error_format_exception',
  noSuchMethodError: 'error_no_such_method_error',
  rangeError: 'error_range_error',
  receiveTimeout: 'error_receive_timeout',
  stateError: 'error_state_error',
  typeError: 'error_type_error',
  unknown: 'error_unknown',
  sendTimeout: 'error_send_timeout',
};

/**
 * `AppExceptionType.formattedName()`.
 *
 * В Dart метод жил на самом перечислении и брал переводы через
 * `getL10n(context)`; здесь перечисление — строковый союз, поэтому метод
 * стал функцией, а переводы берутся вне дерева компонентов: `getL10n()`.
 */
export function appExceptionTypeFormattedName(type: AppExceptionType): string {
  const l10n = getL10n();
  return l10n[NAME_KEY_BY_TYPE[type]];
}

/** Значение поля `name` у ошибки HTTP-слоя — по нему она и опознаётся. */
export const HTTP_ERROR_NAME = 'HttpError';

/**
 * Причины сбоя транспорта. Набор повторяет `DioExceptionType`, чтобы
 * отображение в [AppExceptionType] осталось прежним.
 */
export const HttpErrorType = {
  connectionTimeout: 'connectionTimeout',
  sendTimeout: 'sendTimeout',
  receiveTimeout: 'receiveTimeout',
  badCertificate: 'badCertificate',
  badResponse: 'badResponse',
  cancel: 'cancel',
  connectionError: 'connectionError',
  unknown: 'unknown',
} as const;

export type HttpErrorType = (typeof HttpErrorType)[keyof typeof HttpErrorType];

/** Ответ сервера, если он успел прийти, — замена `Response` из dio. */
export interface HttpErrorResponse {
  readonly status: number;
  readonly data: unknown;
}

/**
 * Ошибка HTTP-слоя: контракт между транспортом и слоем исключений.
 *
 * Это обычный объект, а не наследник `Error`: транспорт собирает его сам,
 * и опознаётся он по полю `name` — так же, как dio опознавался по типу.
 */
export interface HttpError {
  readonly name: typeof HTTP_ERROR_NAME;
  readonly type: HttpErrorType;
  /** Код ответа, когда он известен без разбора тела. */
  readonly statusCode?: number;
  readonly response?: HttpErrorResponse;
  readonly requestUrl: string;
  readonly cause?: unknown;
}

/** `error is DioException` — проверка по форме, а не по классу. */
export function isHttpError(e: unknown): e is HttpError {
  if (typeof e !== 'object' || e === null) return false;
  const candidate = e as { name?: unknown; type?: unknown };
  return candidate.name === HTTP_ERROR_NAME && typeof candidate.type === 'string';
}

/** `fromDioType` — причина транспорта один в один. */
const APP_EXCEPTION_TYPE_BY_HTTP_ERROR_TYPE: Record<HttpErrorType, AppExceptionType> = {
  badCertificate: AppExceptionType.badCertificate,
  badResponse: AppExceptionType.badResponse,
  cancel: AppExceptionType.cancel,
  connectionError: AppExceptionType.connectionError,
  connectionTimeout: AppExceptionType.connectionTimeout,
  receiveTimeout: AppExceptionType.receiveTimeout,
  sendTimeout: AppExceptionType.sendTimeout,
  unknown: AppExceptionType.unknown,
};

/**
 * `AppExceptionType.fromDioType`.
 *
 * `type` приходит из транспорта уже строкой, поэтому чужое значение сюда
 * попасть может — в Dart оно было невозможно по типу. Неизвестная причина
 * трактуется как [AppExceptionType.unknown], а не роняет разбор ошибки.
 */
export function appExceptionTypeFromHttpError(error: HttpError): AppExceptionType {
  return APP_EXCEPTION_TYPE_BY_HTTP_ERROR_TYPE[error.type] ?? AppExceptionType.unknown;
}

/** `StackTrace.current` — стек места вызова. */
export function currentStackTrace(): string {
  return new Error().stack ?? '';
}

/**
 * `stk` из `catch (e, stk)`: в JS стек принадлежит самой ошибке, и только
 * если она наследник `Error`. Иначе берётся стек места перехвата.
 */
export function stackTraceOf(error: unknown): string {
  if (error instanceof Error && typeof error.stack === 'string') return error.stack;
  return currentStackTrace();
}

interface AppExceptionInit<T> {
  readonly stackTrace: string;
  readonly innerError: unknown;
  readonly statusCode?: number | null;
  readonly apiError?: ApiResponseModel<T> | null;
  readonly type?: AppExceptionType;
}

/** Параметры `AppException.handle` — в Dart это именованный аргумент. */
export interface AppExceptionHandleOptions<T> {
  readonly onApiResponseDataFound?: ApiResponseItemParser<T>;
}

export class AppException<T = unknown> extends Error {
  private constructor(init: AppExceptionInit<T>) {
    super();
    this.name = 'AppException';
    this.type = init.type ?? AppExceptionType.unknown;
    this.innerError = init.innerError;
    this.statusCode = init.statusCode ?? null;
    this.stackTrace = init.stackTrace;
    this.apiError = init.apiError ?? null;
  }

  readonly type: AppExceptionType;

  readonly innerError: unknown;

  readonly statusCode: number | null;

  readonly stackTrace: string;

  readonly apiError: ApiResponseModel<T> | null;

  override toString(): string {
    const parts: string[] = [appExceptionTypeFormattedName(this.type)];
    const errorDescription = this.apiError?.message ?? describeError(this.innerError);
    if (errorDescription !== null) {
      parts.push(`\n${errorDescription}`);
    }
    return parts.join('');
  }

  /** A static instance representing an unknown error. */
  static unknown<T = unknown>(innerError: unknown, stackTrace: string): AppException<T> {
    return new AppException<T>({
      stackTrace,
      innerError,
      apiError: null,
      type: AppExceptionType.unknown,
      statusCode: null,
    });
  }

  /** `AppException.handleDioException` — разбор ошибки транспорта. */
  static handleHttpError<T = unknown>(
    error: HttpError,
    stackTrace: string,
    onApiResponseDataFound?: ApiResponseItemParser<T>,
  ): AppException<T> {
    const response = error.response;
    return new AppException<T>({
      stackTrace,
      innerError: error,
      // В Dart код брался только из ответа: у `DioException` своего поля
      // с кодом нет. Здесь транспорт может знать код и без разобранного
      // тела, поэтому он идёт запасным источником — иначе 4xx/5xx
      // потерялись бы при разборе причины сбоя.
      statusCode: response?.status ?? error.statusCode ?? null,
      apiError:
        response !== undefined
          ? apiResponseModelTryFromResponse<T>(response, onApiResponseDataFound)
          : null,
      type: appExceptionTypeFromHttpError(error),
    });
  }

  static handle<T = unknown>(
    error: unknown,
    stackTrace: string,
    options: AppExceptionHandleOptions<T> = {},
  ): AppException<T> {
    if (error instanceof AppException) {
      // Ответ сервера сюда намеренно не переносится — так же, как в Dart:
      // повторная упаковка сохраняет только причину, код и стек.
      return new AppException<T>({
        stackTrace,
        innerError: error.innerError,
        statusCode: error.statusCode,
        type: error.type,
      });
    }

    if (isHttpError(error)) {
      return AppException.handleHttpError<T>(error, stackTrace, options.onApiResponseDataFound);
    }

    if (error instanceof TypeError) {
      return new AppException<T>({
        stackTrace,
        innerError: error,
        type: AppExceptionType.typeError,
      });
    }

    if (error instanceof RangeError) {
      return new AppException<T>({
        stackTrace,
        innerError: error,
        type: AppExceptionType.rangeError,
      });
    }

    // `FormatException` в Dart — это разбор, который не сошёлся. В JS тот
    // же смысл у `SyntaxError` (битый JSON) и у `ZodError` (тело не того
    // формата): и то и другое слой сбоев превращает в `badContract`.
    if (error instanceof SyntaxError || error instanceof ZodError) {
      return new AppException<T>({
        stackTrace,
        innerError: error,
        type: AppExceptionType.formatException,
      });
    }

    return AppException.unknown<T>(error, stackTrace);
  }
}

/** `innerError?.toString()`: у `null` и `undefined` описания нет. */
function describeError(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  return String(error);
}
